<template>
  <div
    class="panel panel-default"
    :class="{ 'recruiting-panel': isRecruitingFilter }"
  >
    <div class="panel-heading" v-if="isRecruitingFilter">
      {{ lang.RCS000 }}
      <span
        id="shopperCounterContainer"
        :class="{ 'text-red': shopperCountExceeded }"
        v-if="typeof resultShopperData.ShopperCount === 'number'"
      >
        <span>{{ lang.RCS001 }}: {{ resultShopperData.ShopperCount }}</span>
        <i v-show="shopperCountExceeded" class="material-icons md-24"
          >warning</i
        >
      </span>
      <b-tooltip
        target="shopperCounterContainer"
        v-if="shopperCountExceeded"
        :delay="globals.tooltipDelay"
        boundary="viewport"
        placement="bottom"
        >{{ lang.RCS030 }}</b-tooltip
      >
    </div>
    <div class="panel-body">
      <div class="filter-border col-md-12">
        <div
          class="form-group col-md-6"
          id="inputShopperFilterNotSameLocationFormGroup"
        >
          <div class="checkbox">
            <label>
              <input
                type="checkbox"
                id="inputShopperFilterNotSameLocation"
                v-model="notSameLocation"
                @change="getShopperFilterResult()"
                :disabled="notWithLocationPropertyGuids.length > 0"
              />
              <span class="checkbox-material">
                <span class="check"></span>
              </span>
              <span>{{ lang.RCS002 }}:</span>
            </label>
          </div>
          <b-tooltip
            target="inputShopperFilterNotSameLocationFormGroup"
            boundary="viewport"
            triggers="hover"
            :delay="globals.tooltipDelay"
            >{{ lang.RCS003 }}</b-tooltip
          >
        </div>
        <div
          class="form-group col-md-3"
          id="inputShopperFilterNotWithinTimespanValueFormGroup"
        >
          <div>
            <input
              type="number"
              id="inputShopperFilterNotWithinTimespanValue"
              step="1"
              min="0"
              max="100000"
              class="form-control"
              v-model.number="notWithinTimespanValue"
              @change="validateTimespanValue(), getShopperFilterResult()"
            />
          </div>
        </div>
        <div
          class="form-group col-md-3"
          id="inputShopperFilterNotWithinTimespanFormGroup"
        >
          <select
            class="form-control"
            id="inputShopperFilterNotWithinTimespan"
            v-model="notWithinTimespan"
            @change="getShopperFilterResult()"
          >
            <option v-for="(span, idx) in timespans" :key="idx" :value="span">
              {{ getTranslationFor(span) }}
            </option>
          </select>
        </div>

        <div
          class="form-group col-md-12"
          id="inputShopperFilterNotWithinLocationPropertiesFormGroup"
          v-if="locationPropertiesList.length > 0"
        >
          <select2
            id="inputShopperFilterNotWithinLocationProperties"
            v-model="notWithLocationPropertyGuids"
            :placeholder="lang.RCS004"
            :allowClear="true"
            :multiple="true"
            @change="(notSameLocation = true), getShopperFilterResult()"
          >
            <option
              v-for="prop in locationPropertiesList"
              :key="prop.LocationPropertyGuid"
              :value="prop.LocationPropertyGuid"
            >
              {{ prop.LocationPropertyName }}
            </option>
          </select2>
          <b-tooltip
            target="inputShopperFilterNotWithinLocationPropertiesFormGroup"
            boundary="viewport"
            triggers="hover"
            :delay="globals.tooltipDelay"
            >{{ lang.RCS005 }}</b-tooltip
          >
        </div>
      </div>

      <div
        class="form-group form-group-dynamic"
        v-for="(filter, index) in dynamicFilters"
        :key="index"
      >
        <div
          class="form-group col-md-3"
          :id="`selectShopperFilterDynamicTypeFormGroup${index}`"
        >
          <select2
            v-model="filter.Type"
            :ref="`filterTypeSelect${index}`"
            :id="`selectShopperFilterDynamicType${index}`"
            :placeholder="`${lang.RCS006}...`"
            :allowClear="true"
            @change="dynamicFilterTypeChanged(filter)"
          >
            <option
              v-for="(type, idx) in filterTypes"
              :key="idx"
              :value="type"
              :disabled="!isTypeEnabled(type, filter)"
            >
              {{ getTranslationFor(type) }}
            </option>
          </select2>
          <b-tooltip
            :target="`selectShopperFilterDynamicTypeFormGroup${index}`"
            boundary="viewport"
            triggers="hover"
            :delay="globals.tooltipDelay"
            >{{ lang.RCS007 }}</b-tooltip
          >
        </div>
        <div
          class="form-group col-md-8"
          v-if="filter.secondLevelSelectVisible"
          :id="`selectShopperFilterDynamicValueFormGroup${index}`"
        >
          <select2
            v-model="filter.Value"
            :ref="`secondLevelSelect${index}`"
            :id="`selectShopperFilterDynamicValue${index}`"
            :placeholder="`${lang.RCS006}...`"
            :allowClear="true"
            :multiple="filter.Type !== 'ShopperProperty'"
            :tags="filter.Type === 'City' || filter.Type === 'PostalCode'"
            :noResultsString="
              filter.Type === 'City' || filter.Type === 'PostalCode'
                ? lang.RCS018
                : undefined
            "
            @change="dynamicFilterValueChanged(filter)"
          >
            <template
              v-if="filter.Type === 'Country' || filter.Type === 'Gender'"
            >
              <option
                v-for="value in filter.currentDynamicFilterValues"
                :key="value[`${filter.Type}Code`]"
                :value="value[`${filter.Type}Code`]"
              >
                {{ value[`${filter.Type}Name`] }}
              </option>
            </template>
            <template v-else-if="filter.Type === 'ShopperProperty'">
              <optgroup
                v-for="topic in filter.currentDynamicFilterValues"
                :key="topic[`${filter.Type}Guid`]"
                :label="topic[`${filter.Type}Name`]"
              >
                <option
                  v-for="property in topic.ChildProperties"
                  :disabled="
                    propertyAlreadyUsed(property[`${filter.Type}Guid`])
                  "
                  :key="property[`${filter.Type}Guid`]"
                  :value="property[`${filter.Type}Guid`]"
                >
                  {{ property[`${filter.Type}Name`] }}
                  <span v-if="property.GroupTitle"
                    >({{ property.GroupTitle }})</span
                  >
                </option>
              </optgroup>
            </template>
            <template v-else>
              <option
                v-for="value in filter.currentDynamicFilterValues"
                :key="value[`${filter.Type}Guid`]"
                :value="value[`${filter.Type}Guid`]"
              >
                {{ value[`${filter.Type}Name`] }}
              </option>
            </template>
          </select2>
          <b-tooltip
            :target="`selectShopperFilterDynamicValueFormGroup${index}`"
            boundary="viewport"
            triggers="hover"
            :delay="globals.tooltipDelay"
            >{{ getTranslationFor(filter.Type) }}</b-tooltip
          >
        </div>
        <div
          class="form-group col-md-4"
          v-if="filter.secondLevelRangeVisible"
          :id="`selectShopperFilterDynamicValueFormGroup${index}`"
        >
          <input
            type="number"
            step="1"
            min="0"
            max="100"
            class="form-control"
            :placeholder="lang.RCS022"
            v-model.number="filter.Value.From"
            @change="dynamicFilterValueChanged(filter)"
          />
        </div>
        <div
          class="form-group col-md-4"
          v-if="filter.secondLevelRangeVisible"
          :id="`selectShopperFilterDynamicValueFormGroup${index}`"
        >
          <input
            type="number"
            step="1"
            min="0"
            max="100"
            class="form-control"
            :placeholder="lang.RCS023"
            v-model.number="filter.Value.To"
            @change="dynamicFilterValueChanged(filter)"
          />
        </div>
        <div
          class="form-group col-md-11"
          v-if="filter.thirdLevelInputVisible"
          :id="`selectShopperFilterDynamicValueValuesFormGroup${index}`"
        >
          <select2
            v-model="filter.Subvalue"
            v-if="
              filter.thirdLevelInputType === 'SingleSelect' ||
              filter.thirdLevelInputType === 'MultiSelect'
            "
            :ref="`thirdLevelSelect${index}`"
            :id="`inputShopperFilterDynamicValueValues${index}`"
            :placeholder="`${lang.RCS006}...`"
            :allowClear="true"
            :multiple="filter.thirdLevelInputType === 'MultiSelect'"
            :tags="true"
            @change="getShopperFilterResult()"
          >
            <option
              v-for="value in filter.currentDynamicFilterValueValues"
              :key="value[`ListValueGuid`]"
              :value="value[`ListValueGuid`]"
            >
              {{ value.IsNoAnswer ? `[${lang.RCS031}]` : value[`ListValue`] }}
            </option>
          </select2>

          <div
            class="checkbox"
            v-else-if="filter.thirdLevelInputType === 'Checkbox'"
          >
            <label>
              <span>{{ lang.RCS008 }}:</span>
              <input
                type="checkbox"
                :id="`inputShopperFilterDynamicValueValues${index}`"
                :value="filter.Subvalue"
                v-model="filter.Subvalue"
                @change="getShopperFilterResult()"
              />
              <span class="checkbox-material">
                <span class="check"></span>
              </span>
            </label>
          </div>

          <div
            v-else-if="
              filter.thirdLevelInputType === 'Text' ||
              filter.thirdLevelInputType === 'Integer'
            "
          >
            <input
              :type="filter.thirdLevelInputType === 'Text' ? 'text' : 'number'"
              :id="`inputShopperFilterDynamicValueValues${index}`"
              step="1"
              class="form-control"
              v-model="filter.Subvalue"
              @change="getShopperFilterResult()"
            />
          </div>

          <b-tooltip
            :target="`selectShopperFilterDynamicValueValuesFormGroup${index}`"
            boundary="viewport"
            triggers="hover"
            :delay="globals.tooltipDelay"
            >{{ lang.RCS009 }}</b-tooltip
          >
        </div>

        <div
          class="col-md-1 pull-right"
          :class="{ 'centerized-remove-button': filter.thirdLevelInputVisible }"
        >
          <button
            type="button"
            class="btn btn-remove-filter"
            @click="removeDynamicFilter(filter)"
          >
            <i class="material-icons md-24 text-red">clear</i>
          </button>
        </div>
      </div>

      <button
        type="button"
        id="addDynamicFilterButton"
        class="btn btn-fab btn-fab-mini btn-default btn-add-filter"
        :disabled="isLastDynamicFilterEmpty()"
        @click="addDynamicFilter()"
      >
        <i class="material-icons md-36 text-green">add</i>
      </button>
      <b-tooltip
        target="addDynamicFilterButton"
        boundary="viewport"
        triggers="hover"
        :delay="globals.tooltipDelay"
        v-if="!isLastDynamicFilterEmpty()"
        >{{ lang.RCS010 }}</b-tooltip
      >
    </div>
  </div>
</template>

<script>
import Select2 from "../Tools/Select2";

const uuidv4 = require("uuid/v4");

export default {
  name: "ShopperFilter",
  components: {
    Select2,
  },
  props: {
    currentFilterObject: {
      Type: Object,
      default: () => ({}),
    },
    locationPropertiesList: {
      Type: Array,
      default: () => [],
    },
    countryList: {
      Type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      notSameLocation: false,
      notWithLocationPropertyGuids: [],
      notWithinTimespanValue: 0,
      notWithinTimespan: "days",
      timespans: ["days", "weeks", "months", "years"],
      dynamicFilters: [],
      shopperPropertiesList: [],
      occupationalGroupList: [],
      isRecruitingFilter: false,
      filterTypes: [
        "Country",
        "City",
        "PostalCode",
        "Gender",
        "Age",
        "OccupationalGroup",
        "ShopperProperty",
      ],
      resultShopperData: {},
      lang: {
        RCS000: "Shopper filter",
        RCS001: "Available Shoppers",
        RCS002: "Not same location for",
        RCS003:
          "Select only shoppers that did not visit the location of the check within a given time period",
        RCS004: "Not within location properties... (optional)",
        RCS005:
          "Select only shoppers that did not visit any location where the selected location properties have the same value as for the locations that the shopper already visited",
        RCS006: "Choose",
        RCS007: "Type of filter",
        RCS008: "Value",
        RCS009: "Select Value(s)",
        RCS010: "Add filter",
        RCS011: "Shopper property data request failed",
        RCS012: "Shopper data request failed",
        RCS013: "Shopper property",
        RCS014: "days",
        RCS015: "weeks",
        RCS016: "months",
        RCS017: "years",
        RCS018: "Please type in a value",
        RCS019: "male",
        RCS020: "female",
        RCS021: "divers",
        RCS022: "from",
        RCS023: "to",
        RCS024: "Country",
        RCS025: "City",
        RCS026: "Postal code",
        RCS027: "Gender",
        RCS028: "Age",
        RCS029: "Occupational group",
        RCS030:
          "The maximum count of shoppers that can be shown on the map is exceeded. If you want to see all results on the map, please define some more filters.",
        RCS031: "No answer",
      },
    };
  },
  computed: {
    shopperCountExceeded() {
      if (
        !this.resultShopperData ||
        _.isEmpty(this.resultShopperData) ||
        !this.resultShopperData.Shoppers
      )
        return false;
      return (
        this.resultShopperData.ShopperCount >
        this.resultShopperData.Shoppers.length
      );
    },
  },
  watch: {
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this, this.debouncedUpdateSelect2Data);
      this.globals.getOccupationalGroupList((data) => {
        this.occupationalGroupList = [];
        for (let guid in data) {
          this.occupationalGroupList.push({
            OccupationalGroupGuid: guid,
            OccupationalGroupName: data[guid],
          });
        }
        this.$emit("OccupationalGroupsReceived");
        this.debouncedUpdateSelect2Data();
      });
    },
    resultShopperData(newData) {
      this.$emit("shopperDataChanged", newData);
    },
  },
  created() {
    this.isRecruitingFilter = this.$route.name === "Recruiting";
    this.debouncedUpdateSelect2Data = _.debounce(this.updateSelect2Data, 500);
  },
  mounted() {
    this.readComponentSettingsFromStore();

    if (this.globals.selectedLanguage) {
      this.globals.getLanguageStrings(this, this.debouncedUpdateSelect2Data);
      this.globals.getOccupationalGroupList((data) => {
        this.occupationalGroupList = [];
        for (let guid in data) {
          this.occupationalGroupList.push({
            OccupationalGroupGuid: guid,
            OccupationalGroupName: data[guid],
          });
        }
        this.$emit("OccupationalGroupsReceived");
      });
    }
    this.getShopperProperties();
    this.getShopperFilterResult();
  },
  destroyed() {
    this.writeComponentSettingsToStore();
  },
  methods: {
    readComponentSettingsFromStore() {
      if (
        !this.$store.getters["userSettings/rememberFilters"] ||
        !this.isRecruitingFilter
      )
        return;

      this.$filterRestorePending = true;
      let restoreDone = () => {
        this.$nextTick(() => {
          delete this.$filterRestorePending;
          this.debouncedUpdateSelect2Data();
        });
      };

      let restoreFilters = () => {
        this.$nextTick(() => {
          let storedData = this.$store.getters[
            "localFilters/recruiting_shopperFilter"
          ];
          if (storedData) {
            for (let key in storedData) {
              if (_.has(this, key)) this[key] = _.cloneDeep(storedData[key]);
            }
          }
          restoreDone();
        });
      };

      let eventsToWaitFor = ["ShopperPropertiesReceived"];

      if (this.countryList.length < 1) {
        eventsToWaitFor.push("CountryListReceived");
        this.$parent.$once("CountryListReceived", () =>
          this.$emit("CountryListReceived")
        );
      }

      if (this.locationPropertiesList.length < 1) {
        eventsToWaitFor.push("LocationPropertiesReceived");
        this.$parent.$once("LocationPropertiesReceived", () =>
          this.$emit("LocationPropertiesReceived")
        );
      }

      if (this.occupationalGroupList.length < 1)
        eventsToWaitFor.push("OccupationalGroupsReceived");

      this.globals.doAfterEvents(eventsToWaitFor, restoreFilters, this);
    },
    writeComponentSettingsToStore() {
      if (
        !this.$store.getters["userSettings/rememberFilters"] ||
        !this.isRecruitingFilter
      )
        return;
      this.$store.commit("localFilters/recruiting_shopperFilter", {
        notSameLocation: this.notSameLocation,
        notWithLocationPropertyGuids: this.notWithLocationPropertyGuids,
        notWithinTimespanValue: this.notWithinTimespanValue,
        notWithinTimespan: this.notWithinTimespan,
        dynamicFilters: this.dynamicFilters,
      });
    },
    updateSelect2Data() {
      for (let filter of this.dynamicFilters) {
        if (filter.Type === "Gender")
          filter.currentDynamicFilterValues = [
            { GenderName: this.lang.RCS019, GenderCode: "M" },
            { GenderName: this.lang.RCS020, GenderCode: "F" },
            { GenderName: this.lang.RCS021, GenderCode: "D" },
          ];
        else if (filter.Type === "Country")
          filter.currentDynamicFilterValues = this.countryList;
        else if (filter.Type === "OccupationalGroup")
          filter.currentDynamicFilterValues = this.occupationalGroupList;
        else if (filter.Type === "ShopperProperty")
          filter.currentDynamicFilterValues = this.shopperPropertiesList;
      }
      _.remove(this.notWithLocationPropertyGuids, (selectedValue) => {
        return (
          _.findIndex(
            this.locationPropertiesList,
            (val) => val.LocationPropertyGuid === selectedValue
          ) < 0
        );
      });
      this.$forceUpdate();

      this.$nextTick(() => {
        for (let refName in this.$refs) {
          if (this.$refs[refName][0])
            for (let component of this.$refs[refName]) {
              if (component.reinit) component.reinit();
            }
          else if (this.$refs[refName].reinit) this.$refs[refName].reinit();
        }
      });

      this.getShopperFilterResult();
    },
    updateSelect2Values() {
      for (let refName in this.$refs) {
        if (this.$refs[refName][0])
          for (let component of this.$refs[refName]) {
            if (component.updateValue) component.updateValue();
          }
        else if (this.$refs[refName].updateValue)
          this.$refs[refName].updateValue();
      }
    },
    getTranslationFor(text) {
      switch (text) {
        case "ShopperProperty":
          return this.lang.RCS013;
        case "Country":
          return this.lang.RCS024;
        case "City":
          return this.lang.RCS025;
        case "PostalCode":
          return this.lang.RCS026;
        case "Gender":
          return this.lang.RCS027;
        case "Age":
          return this.lang.RCS028;
        case "OccupationalGroup":
          return this.lang.RCS029;
        case "days":
          return this.lang.RCS014;
        case "weeks":
          return this.lang.RCS015;
        case "months":
          return this.lang.RCS016;
        case "years":
          return this.lang.RCS017;
      }
      return text;
    },
    clearShopperFilter() {
      this.notSameLocation = false;
      this.notWithLocationPropertyGuids = [];
      this.notWithinTimespanValue = 0;
      this.notWithinTimespan = "days";
      this.dynamicFilters = [];
      this.getShopperFilterResult();
    },
    validateTimespanValue() {
      if (this.notWithinTimespanValue < 0) this.notWithinTimespanValue = 0;
      else if (this.notWithinTimespanValue > 100000)
        this.notWithinTimespanValue = 100000;
      this.notWithinTimespanValue = Math.floor(this.notWithinTimespanValue);
    },
    addDynamicFilter() {
      this.dynamicFilters.push({
        Guid: uuidv4(),
        Type: "",
      });
    },
    removeDynamicFilter(filter) {
      _.remove(this.dynamicFilters, (f) => f.Guid === filter.Guid);
      this.getShopperFilterResult();
      this.$forceUpdate();
      this.updateSelect2Values();
    },
    isLastDynamicFilterEmpty() {
      if (this.dynamicFilters.length < 1) return false;
      return !this.dynamicFilters[this.dynamicFilters.length - 1].Type;
    },
    isTypeEnabled(type, filter) {
      if (type === "ShopperProperty") return true;

      for (let filter of this.dynamicFilters) {
        if (filter.Type === type) return false;
      }
      return true;
    },
    propertyAlreadyUsed(guid) {
      for (let filter of this.dynamicFilters) {
        if (filter.Value === guid) return true;
      }
      return false;
    },
    dynamicFilterTypeChanged(filter) {
      delete filter.Value;
      delete filter.ValuesValue;
      delete filter.currentDynamicFilterValues;
      delete filter.currentDynamicFilterValueValues;
      delete filter.secondLevelSelectVisible;
      delete filter.secondLevelRangeVisible;

      if (filter.Type === "Country") {
        filter.currentDynamicFilterValues = this.countryList;
        filter.secondLevelSelectVisible = true;
      } else if (filter.Type === "City" || filter.Type === "PostalCode") {
        filter.currentDynamicFilterValues = [];
      } else if (filter.Type === "Gender") {
        filter.currentDynamicFilterValues = [
          { GenderName: this.lang.RCS019, GenderCode: "M" },
          { GenderName: this.lang.RCS020, GenderCode: "F" },
          { GenderName: this.lang.RCS021, GenderCode: "D" },
        ];
      } else if (filter.Type === "OccupationalGroup") {
        filter.currentDynamicFilterValues = this.occupationalGroupList;
      } else if (filter.Type === "Age") {
        filter.secondLevelRangeVisible = true;
        filter.Value = {};
        return;
      } else if (filter.Type === "ShopperProperty") {
        filter.currentDynamicFilterValues = this.shopperPropertiesList;
      } else return;

      this.$nextTick(() => {
        filter.secondLevelSelectVisible = true;
        this.$forceUpdate();
      });
    },
    dynamicFilterValueChanged(filter) {
      // Forces select2 to rerender with new options
      delete filter.Subvalue;
      delete filter.thirdLevelInputVisible;

      if (filter.Type !== "ShopperProperty") {
        this.getShopperFilterResult();
        return;
      }

      this.$nextTick(() => {
        if (!filter.Value) return;
        let currVal = "";

        if (filter.Type === "ShopperProperty") {
          for (let topic of filter.currentDynamicFilterValues) {
            currVal = _.find(topic.ChildProperties, (val) => {
              return val[`${filter.Type}Guid`] === filter.Value;
            });
            if (currVal) break;
          }
        } else
          currVal = _.find(filter.currentDynamicFilterValues, (val) => {
            return val[`${filter.Type}Guid`] === filter.Value;
          });

        if (currVal) {
          filter.thirdLevelInputType = currVal.PropertyType;
          if (currVal.PropertyType === "SingleSelect")
            filter.thirdLevelInputType = "MultiSelect";
          filter.thirdLevelInputVisible = true;

          if (filter.thirdLevelInputType === "Checkbox") {
            filter.Subvalue = true;
          }
          if (
            (filter.thirdLevelInputType === "MultiSelect" ||
              filter.thirdLevelInputType === "SingleSelect") &&
            currVal[`${filter.Type}Values`] &&
            currVal[`${filter.Type}Values`].length > 0
          ) {
            filter.currentDynamicFilterValueValues =
              currVal[`${filter.Type}Values`];

            for (let value of filter.currentDynamicFilterValueValues) {
              if (value.ListCustomValue) {
                value.ListValueGuid = value.ListCustomValue;
                value.ListValue = value.ListCustomValue;
                delete value.ListCustomValue;
              }
            }
          } else this.getShopperFilterResult();
          this.$forceUpdate();
        }
      });
    },
    collectFilterData() {
      let result = {};

      if (this.notSameLocation) {
        result.NotSameLocation = this.notSameLocation;
        result.NotWithinDays = this.notWithinTimespanValue;
        if (this.notWithinTimespan === "days") result.NotWithinDays *= 1;
        else if (this.notWithinTimespan === "weeks") result.NotWithinDays *= 7;
        else if (this.notWithinTimespan === "months")
          result.NotWithinDays *= 30;
        else if (this.notWithinTimespan === "years")
          result.NotWithinDays *= 365;

        if (this.notWithLocationPropertyGuids.length > 0)
          result.NotWithLocationPropertyGuids = this.notWithLocationPropertyGuids;
      }

      for (let filter of this.dynamicFilters) {
        if (!filter.Type || !filter.Value) continue;

        if (filter.Type === "Country") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.CountryCodes = filter.Value;
        } else if (filter.Type === "City") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.Cities = filter.Value;
        } else if (filter.Type === "PostalCode") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.PostalCodes = filter.Value;
        } else if (filter.Type === "Gender") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.Genders = filter.Value;
        } else if (filter.Type === "OccupationalGroup") {
          if (!filter.Value || filter.Value.length < 1) continue;
          result.OccupationalGroups = filter.Value;
        } else if (filter.Type === "Age") {
          if (!filter.Value || _.isEmpty(filter.Value)) continue;
          if (!filter.Value.From) delete filter.Value.From;
          if (!filter.Value.To) delete filter.Value.To;

          if (filter.Value.From < 0) filter.Value.From = 0;
          if (filter.Value.From > 100) filter.Value.From = 100;
          if (filter.Value.To < 0) filter.Value.To = 0;
          if (filter.Value.To > 100) filter.Value.To = 100;

          if (
            filter.Value.From &&
            filter.Value.To &&
            filter.Value.From > filter.Value.To
          )
            [filter.Value.From, filter.Value.To] = [
              filter.Value.To,
              filter.Value.From,
            ];

          if (!_.isEmpty(filter.Value)) result.Age = filter.Value;
        } else if (filter.Type === "ShopperProperty") {
          if (!filter.Value || typeof filter.Subvalue === "undefined") continue;

          let filterObject = {
            ShopperPropertyGuid: filter.Value,
          };

          if (filter.thirdLevelInputType === "MultiSelect") {
            if (filter.Subvalue.length < 1) continue;
            let guids = _.groupBy(filter.Subvalue, (val) => {
              return !!val.match(
                /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
              );
            });
            if (guids.true) filterObject.ListValueGuids = guids.true;
            if (guids.false) filterObject.ListCustomValues = guids.false;
          } else if (filter.thirdLevelInputType === "SingleSelect") {
            if (
              !!filter.Subvalue.match(
                /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
              )
            )
              filterObject.ListValueGuids = [filter.Subvalue];
            else filterObject.ListCustomValues = [filter.Subvalue];
          } else if (filter.thirdLevelInputType === "Checkbox") {
            filterObject.BitValue = filter.Subvalue;
          } else {
            // Text and Integer
            filterObject.TextValues = _.words(filter.Subvalue);
          }

          if (!result.ShopperProperties) result.ShopperProperties = [];
          result.ShopperProperties.push(filterObject);
        }
      }

      this.$emit("filterUpdate", result);
    },
    restoreFilterSettings() {
      this.clearShopperFilter();
      let filterObject = this.currentFilterObject;
      if (_.isEmpty(filterObject)) return;

      this.notSameLocation =
        typeof filterObject.NotSameLocation === "boolean"
          ? filterObject.NotSameLocation
          : false;
      this.notWithLocationPropertyGuids =
        filterObject.NotWithLocationPropertyGuids || [];
      this.notWithinTimespanValue = filterObject.NotWithinDays || 0;
      if (
        this.notWithinTimespanValue &&
        this.notWithinTimespanValue % 365 === 0
      ) {
        this.notWithinTimespanValue /= 365;
        this.notWithinTimespan = "years";
      } else if (
        this.notWithinTimespanValue &&
        this.notWithinTimespanValue % 30 === 0
      ) {
        this.notWithinTimespanValue /= 30;
        this.notWithinTimespan = "months";
      } else if (
        this.notWithinTimespanValue &&
        this.notWithinTimespanValue % 7 === 0
      ) {
        this.notWithinTimespanValue /= 7;
        this.notWithinTimespan = "weeks";
      } else this.notWithinTimespan = "days";

      for (let type of this.filterTypes) {
        let apiType = this.getApiPropertyNameForFilterType(type);
        if (filterObject[apiType]) {
          if (type === "ShopperProperty") {
            for (let prop of filterObject.ShopperProperties) {
              let subvalue =
                prop.ListValueGuids ||
                prop.ListCustomValues ||
                prop.TextValues ||
                prop.BitValue;

              if (prop.ListValueGuids && prop.ListCustomValues)
                subvalue = _.concat(prop.ListValueGuids, prop.ListCustomValues);

              this.createDynamicFilter(
                type,
                prop.ShopperPropertyGuid,
                subvalue
              );
            }
          } else {
            this.createDynamicFilter(type, filterObject[apiType]);
          }
        }
      }
      this.updateSelect2Values();
    },
    createDynamicFilter(type, value, subvalue) {
      let filter = {
        Guid: uuidv4(),
        Type: type,
      };
      this.dynamicFilterTypeChanged(filter);
      filter.Value = value;
      this.dynamicFilterValueChanged(filter);
      this.$nextTick(() => {
        if (typeof subvalue !== "undefined") filter.Subvalue = subvalue;
      });
      this.dynamicFilters.push(filter);
    },
    getApiPropertyNameForFilterType(filterType) {
      switch (filterType) {
        case "Country":
          return "CountryCodes";
        case "City":
          return "Cities";
        case "PostalCode":
          return "PostalCodes";
        case "Gender":
          return "Genders";
        case "Age":
          return "Age";
        case "OccupationalGroup":
          return "OccupationalGroups";
        case "ShopperProperty":
          return "ShopperProperties";
        default:
          return "";
      }
    },
    getShopperProperties() {
      let query = `api/Recruiting/Data/ShopperProperties`;
      $.ajax({
        method: "GET",
        url: query,
        success: (data) => {
          this.shopperPropertiesList = data;
          if (!data || _.isEmpty(data)) this.shopperPropertiesList = [];

          for (let topic of this.shopperPropertiesList) {
            topic.ShopperPropertyName = topic.Title;
            topic.ShopperPropertyType = topic.Type;
            delete topic.Title;
            delete topic.Type;

            if (topic.ChildProperties)
              for (let property of topic.ChildProperties) {
                property.ShopperPropertyName = property.Title;
                property.ShopperPropertyType = property.Type;
                property.ShopperPropertyValues = property.AvailableValues;
                delete property.Title;
                delete property.Type;
                delete property.AvailableValues;
              }
          }
          this.$emit("ShopperPropertiesReceived");
        },
        error: () => {
          console.error("Error on GET in getShopperProperties()");
          this.globals.showErrorSnackbar(this.lang.RCS011);
        },
      });
    },
    getShopperFilterResult() {
      if (!this.isRecruitingFilter || this.$filterRestorePending) return;
      this.collectFilterData();
      let query = `api/Recruiting/Shoppers`;
      let postData = this.currentFilterObject;

      $.ajax({
        method: "POST",
        url: query,
        data: JSON.stringify(postData),
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        success: (data) => {
          this.resultShopperData = data;
        },
        error: () => {
          console.error("Error on POST in getShopperFilterResult()");
          this.globals.showErrorSnackbar(this.lang.RCS012);
        },
      });
    },
  },
};
</script>

<style scoped>
.panel {
  height: 100%;
  margin: 0;
}
.panel-heading {
  height: 30px;
  padding: 5px;
  font-weight: bold;
  font-size: 18px;
  background: #cbddf3 !important;
}
.panel-heading > span {
  font-size: 14px;
}
.panel-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
.recruiting-panel .panel-body {
  max-height: none;
  height: calc(50vh - 85px);
}

#shopperCounterContainer {
  position: absolute;
  right: 10px;
  font-weight: 300;
  color: rgb(17, 0, 201);
}
#shopperCounterContainer.text-red {
  cursor: pointer;
}
#shopperCounterContainer.text-red > span {
  position: relative;
  top: -8px;
}
#shopperCounterContainer .material-icons {
  position: relative;
  top: -2px;
}

.form-group {
  overflow: hidden;
  margin: 0;
  padding: 0 5px;
}

.form-group-dynamic {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 0;
  margin: 5px;
}
.filter-border {
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 0;
  margin: 5px;
  width: calc(100% - 10px);
}

.btn-remove-filter {
  padding: 0px;
  margin: 10px;
}
.centerized-remove-button {
  bottom: 23px;
}

.btn-add-filter {
  margin: 10px;
}
.btn-add-filter > i {
  margin-left: -6px;
}

.form-group .checkbox {
  padding-left: 10px;
  text-align: left;
}
.form-group .checkbox > label {
  color: grey;
}
.form-group-dynamic input {
  padding-left: 8px;
  margin: 1px 0 6px;
}

#inputShopperFilterNotSameLocation + span {
  top: 1px;
}
#inputShopperFilterNotSameLocation + span + span {
  margin-left: 5px;
}
</style>



// WEBPACK FOOTER //
// ShopperFilter.vue?270dcdf0